// CLOSED-LOOPS: the Inbox's Commitments card — what is asked of me and what I asked, each with
// its state, due time and who it waits on, and the one-click moves the server offers (`moves`):
// accept / decline, hand in, check a result, can't do it (a failure with options), forward,
// extend, withdraw, and a decision's options (the failure in brief, the recommended one marked).
// Quick actions open small modals (commitment-dialogs.tsx). `focus` (the Inbox's
// `?commitment=<id>`, from an inbox item) opens that commitment's details, closed ones too, and
// brings its row into view. Nothing open and nothing focused: no card.
import { Badge, Button, InfoTip, Segmented, toast } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { problemText } from "../settings/problem-text";
import {
  DetailDialog,
  ExtendDialog,
  FailDialog,
  ForwardDialog,
  type TextAsk,
  TextDialog,
} from "./commitment-dialogs";
import {
  type Commitment,
  type CommitmentActionBody,
  type CommitmentSide,
  commitmentKeys,
  commitmentsQuery,
  useCommitmentAct,
} from "./commitment-queries";

type Tone = "accent" | "info" | "warning" | "danger" | "neutral" | "success";
type Chip = { label: string; tone: Tone; tip: string };

/** The state in a short label, with what it means behind (i). */
export function chipOf(c: Commitment): Chip {
  const mine = c.moves.some((m) => m === "accept" || m === "choose" || m === "submit");
  switch (c.state) {
    case "submitted":
      return mine
        ? {
            label: "Needs you",
            tone: "accent",
            tip:
              c.kind === "decision"
                ? "A decision you owe: pick an option."
                : "Asked of you: accept it or decline it.",
          }
        : { label: "Sent", tone: "neutral", tip: "Not accepted yet." };
    case "working":
      return c.kind === "decision" && mine
        ? { label: "Needs you", tone: "accent", tip: "A decision you owe: pick an option." }
        : { label: "Working", tone: "info", tip: "Accepted and in progress." };
    case "input_required":
    case "auth_required":
      return {
        label: "Waiting",
        tone: "warning",
        tip:
          c.state === "auth_required"
            ? "Blocked on access someone must grant."
            : "Blocked on an answer or input.",
      };
    case "in_verification":
      return { label: "To check", tone: "info", tip: "Handed in; the result is being checked." };
    case "completed":
      return { label: "Done", tone: "success", tip: "Done and checked." };
    case "failed":
      return {
        label: "Failed",
        tone: "danger",
        tip: "It failed; a decision on what next is owed.",
      };
    case "rejected":
      return { label: "Declined", tone: "neutral", tip: "The owner declined it." };
    case "canceled":
      return { label: "Withdrawn", tone: "neutral", tip: "Withdrawn by the requester." };
    case "expired":
      return { label: "Expired", tone: "danger", tip: "Nobody acted in time." };
  }
}

const span = (ms: number) => {
  const m = Math.max(1, Math.round(ms / 60_000));
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h}h` : `${Math.round(h / 24)}d`;
};

/** "due in 3h" / "overdue 2h". */
export function dueText(dueAt: string, now = Date.now()): string {
  const d = Date.parse(dueAt) - now;
  return d >= 0 ? `due in ${span(d)}` : `overdue ${span(-d)}`;
}

const when = (at: string) =>
  new Date(at).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" });

export function CommitmentsCard({
  api,
  company,
  focus = null,
  onFocusClose,
}: {
  api: Api;
  company: CompanyAccess;
  /** A commitment to open (an inbox item's): its details, and its row in view when listed. */
  focus?: string | null;
  onFocusClose?: () => void;
}) {
  const co = company.id;
  const [side, setSide] = useState<CommitmentSide>("of_me");
  const ofMe = useQuery(commitmentsQuery(api, co, "of_me"));
  const byMe = useQuery(commitmentsQuery(api, co, "by_me"));
  const focused = useQuery({
    queryKey: [...commitmentKeys.all(co), "one", focus],
    queryFn: () => api.client.commitments.get({ params: { co, id: focus as string } }),
    enabled: !!focus,
    retry: false,
  });
  const inByMe = !!focus && !!byMe.data?.data.some((c) => c.id === focus);
  const inOfMe = !!focus && !!ofMe.data?.data.some((c) => c.id === focus);
  // the focused row's tab, then the row in view
  useEffect(() => {
    if (inByMe && !inOfMe) setSide("by_me");
    else if (inOfMe) setSide("of_me");
  }, [inByMe, inOfMe]);
  useEffect(() => {
    if (!focus || !(inByMe || inOfMe)) return;
    document.querySelector(`[data-commitment='${focus}']`)?.scrollIntoView?.({ block: "nearest" });
  }, [focus, inByMe, inOfMe]);
  const counts = { of_me: ofMe.data?.data.length ?? 0, by_me: byMe.data?.data.length ?? 0 };
  const detail = focus ? focused.data : undefined;
  if (!counts.of_me && !counts.by_me && !detail) return null;
  const rows = (side === "of_me" ? ofMe : byMe).data?.data ?? [];
  return (
    <section
      data-slot="commitments-card"
      aria-label="Commitments"
      className="mx-4 mt-3 shrink-0 rounded-md border border-subtle bg-surface px-3 py-2.5"
    >
      <header className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold">Commitments</h2>
        <InfoTip label="About commitments">
          Every request with an owner, a due time and a check. Overdue ones escalate on their own.
        </InfoTip>
        <Segmented
          size="sm"
          aria-label="Whose commitments"
          className="ml-auto"
          value={side}
          onValueChange={setSide}
          options={[
            { value: "of_me", label: `Asked of me${counts.of_me ? ` ${counts.of_me}` : ""}` },
            { value: "by_me", label: `I asked${counts.by_me ? ` ${counts.by_me}` : ""}` },
          ]}
        />
      </header>
      {rows.length ? (
        <ul className="mt-2 max-h-80 divide-y divide-subtle overflow-y-auto">
          {rows.map((c) => (
            <CommitmentRow
              key={c.id}
              api={api}
              co={co}
              c={c}
              side={side}
              focused={c.id === focus}
            />
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-[13px] text-muted-foreground">Nothing open here.</p>
      )}
      {detail ? (
        <DetailDialog c={detail} chip={chipOf(detail)} onClose={() => onFocusClose?.()} />
      ) : null}
    </section>
  );
}

type Open =
  | { kind: "text"; ask: TextAsk; body: (text: string) => CommitmentActionBody }
  | { kind: "fail" | "forward" | "extend" | "detail" };

function CommitmentRow({
  api,
  co,
  c,
  side,
  focused,
}: {
  api: Api;
  co: string;
  c: Commitment;
  side: CommitmentSide;
  focused: boolean;
}) {
  const act = useCommitmentAct(api, co);
  const [open, setOpen] = useState<Open | null>(null);
  const chip = chipOf(c);
  const can = (m: Commitment["moves"][number]) => c.moves.includes(m);
  const busy = act.isPending;
  const run = (body: CommitmentActionBody, done: string, after?: () => void) =>
    act.mutate(
      { id: c.id, body },
      {
        onSuccess: () => {
          toast.add({ title: done, type: "success" });
          after?.();
        },
        onError: (e) => toast.add({ title: problemText(e, "Could not update it."), type: "error" }),
      },
    );
  const send = (done: string) => (body: CommitmentActionBody) =>
    run(body, done, () => setOpen(null));
  const text = (ask: TextAsk, body: (t: string) => CommitmentActionBody) =>
    setOpen({ kind: "text", ask, body });
  const who = side === "of_me" ? `from ${c.requester.name}` : `to ${c.owner.name}`;
  const small = (label: string, onClick: () => void, variant: "default" | "outline" | "ghost") => (
    <Button size="sm" variant={variant} disabled={busy} onClick={onClick}>
      {label}
    </Button>
  );
  return (
    <li
      data-commitment={c.id}
      data-focused={focused || undefined}
      className="py-2 text-[13px] data-focused:bg-primary-soft/40"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium">{c.intent}</span>
        <span className="inline-flex items-center">
          <Badge variant={chip.tone} data-slot="commitment-state">
            {chip.label}
          </Badge>
          <InfoTip label={`About ${chip.label}`}>{chip.tip}</InfoTip>
        </span>
      </div>
      <p className="text-secondary">
        {who} · {dueText(c.due_at)}
        {c.waiting_on
          ? ` · waiting on ${c.waiting_on.name}${c.waiting_since ? ` since ${when(c.waiting_since)}` : ""}`
          : ""}
      </p>
      {can("choose") ? (
        <DecisionBlock c={c} pending={busy} onChoose={run} />
      ) : (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {can("accept")
            ? small("Accept", () => run({ action: "accept" }, "Accepted"), "default")
            : null}
          {can("reject")
            ? small(
                "Decline",
                () =>
                  text(
                    { title: "Decline", label: "Why", submit: "Decline", required: true },
                    (t) => ({ action: "reject", reason: t }),
                  ),
                "outline",
              )
            : null}
          {can("submit")
            ? small(
                "Done",
                () =>
                  text(
                    { title: "Hand in", label: "Result", submit: "Hand in", required: true },
                    (t) => ({ action: "submit", result: t }),
                  ),
                "default",
              )
            : null}
          {can("verify") ? (
            <>
              {small(
                "Looks right",
                () => run({ action: "verify", pass: true }, "Marked done"),
                "default",
              )}
              {small(
                "Send back",
                () =>
                  text(
                    {
                      title: "Send back",
                      label: "What misses",
                      submit: "Send back",
                      required: true,
                    },
                    (t) => ({ action: "verify", pass: false, feedback: t }),
                  ),
                "outline",
              )}
            </>
          ) : null}
          {can("fail") ? small("Can't do it", () => setOpen({ kind: "fail" }), "ghost") : null}
          {can("forward") ? small("Forward", () => setOpen({ kind: "forward" }), "ghost") : null}
          {can("extend") ? small("Extend", () => setOpen({ kind: "extend" }), "ghost") : null}
          {can("cancel")
            ? small(
                "Withdraw",
                () =>
                  text(
                    {
                      title: "Withdraw",
                      label: "Why (optional)",
                      submit: "Withdraw",
                      required: false,
                    },
                    (t) => ({ action: "cancel", ...(t ? { reason: t } : {}) }),
                  ),
                "ghost",
              )
            : null}
          {small("Details", () => setOpen({ kind: "detail" }), "ghost")}
        </div>
      )}
      {c.state === "in_verification" && c.result ? (
        <p className="mt-1 line-clamp-2 text-muted-foreground">Result: {c.result}</p>
      ) : null}
      {open?.kind === "text" ? (
        <TextDialog
          ask={open.ask}
          pending={busy}
          onCancel={() => setOpen(null)}
          onSubmit={(t) => send(`${open.ask.submit}: sent`)(open.body(t))}
        />
      ) : open?.kind === "fail" ? (
        <FailDialog pending={busy} onCancel={() => setOpen(null)} onSend={send("Sent")} />
      ) : open?.kind === "forward" ? (
        <ForwardDialog
          api={api}
          co={co}
          c={c}
          pending={busy}
          onCancel={() => setOpen(null)}
          onSend={send("Forwarded")}
        />
      ) : open?.kind === "extend" ? (
        <ExtendDialog
          c={c}
          pending={busy}
          onCancel={() => setOpen(null)}
          onSend={send("Extended")}
        />
      ) : open?.kind === "detail" ? (
        <DetailDialog c={c} chip={chip} onClose={() => setOpen(null)} />
      ) : null}
    </li>
  );
}

function DecisionBlock({
  c,
  pending,
  onChoose,
}: {
  c: Commitment;
  pending: boolean;
  onChoose: (body: CommitmentActionBody, done: string, after?: () => void) => void;
}) {
  const [picked, setPicked] = useState<NonNullable<Commitment["options"]>[number] | null>(null);
  const options = c.options ?? c.failure?.options ?? [];
  return (
    <div data-slot="commitment-decision" className="mt-1.5 grid gap-1.5">
      {c.failure_text ? (
        <p className="rounded-sm bg-sunken px-2 py-1.5 text-secondary">{c.failure_text}</p>
      ) : null}
      <ul className="grid gap-1">
        {options.map((o) => (
          <li key={o.key} className="flex flex-wrap items-center gap-1.5">
            <Button
              size="sm"
              variant={o.recommended ? "default" : "outline"}
              disabled={pending}
              onClick={() => setPicked(o)}
            >
              {o.label}
            </Button>
            {o.recommended ? (
              <span className="inline-flex items-center">
                <Badge variant="accent">Recommended</Badge>
                <InfoTip label="Why recommended">
                  {c.failure?.recommendation || o.detail || "The owner's pick."}
                </InfoTip>
              </span>
            ) : o.detail ? (
              <InfoTip label={`About ${o.label}`}>{o.detail}</InfoTip>
            ) : null}
            {c.default_option === o.key ? (
              <span className="text-xs text-muted-foreground">Default after deadline</span>
            ) : null}
          </li>
        ))}
      </ul>
      {picked ? (
        <TextDialog
          ask={{ title: picked.label, label: "Note (optional)", submit: "Choose", required: false }}
          pending={pending}
          onCancel={() => setPicked(null)}
          onSubmit={(t) =>
            onChoose(
              { action: "choose", option: picked.key, ...(t ? { note: t } : {}) },
              "Decided",
              () => setPicked(null),
            )
          }
        />
      ) : null}
    </div>
  );
}
